/**
 * Connector platform, knowledge-graph view, MCP sign-in and connector-import events.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const CONNECTOR_EVENTS = {
  // Connectors platform — managed MCP gateway. `toolkit_key` (snake-case
  // gateway slug) is the dimension we cohort on; `deduped` on save tells
  // us whether the user re-saved an existing record.
  CONNECTOR_CONNECT_STARTED: 'Connector Connect Started',
  CONNECTOR_CONNECTED: 'Connector Connected',
  CONNECTOR_DISCONNECTED: 'Connector Disconnected',
  // Provider-level workspace connectors. `provider` is google | microsoft.
  // Viewed fires once per provider row per mount; Import Opened carries only
  // stable enums (never account labels or imported content).
  MASTER_CONNECTOR_VIEWED: 'Master Connector Viewed',
  CONNECTOR_IMPORT_OPENED: 'Connector Import Opened',
  // Backend-emitted, from two sites: the `connector_save_external` MCP tool
  // (`via: 'agent_tool'`) and `POST /api/connectors/save`
  // (`via: 'rest_api'`). Properties: `external_source`, `deduped`, `via`.
  // `external_source` is CLAMPED to the known producer set —
  // RESEARCH_SOURCES + SUPPORTED_TOOLKIT_KEYS, anything else `'other'` — by
  // `backend/services/connectors/external-source-analytics.ts`. The
  // underlying provenance field is free text (model-authored on the tool
  // path, request-body on the REST path) and PostHog is
  // `baa_covered: false`, so the raw value must never egress.
  CONNECTOR_SAVE_TO_BOWER: 'Connector Save To Bower',
  CONNECTOR_TOGGLED_IN_CHAT: 'Connector Toggled In Chat',
  // Per-chat "Public sources" master switch. Gates web search plus the
  // eight scholarly-source MCP tools and the scholarly_search /
  // citation_trail meta-tools. Constant renamed from
  // WEB_SEARCH_TOGGLED_IN_CHAT when its scope broadened beyond web
  // search. EMITTED STRING is intentionally unchanged so PostHog
  // dashboards / cohorts / insights keyed on the historical event name
  // keep working — only the JS-side identifier changes.
  PUBLIC_SOURCES_TOGGLED_IN_CHAT: 'Web Search Toggled In Chat',
  // Adjacent CTAs on the Connectors tab — measure the in-product nudge
  // toward saving an ORCID iD, and capture connector wishlist demand
  // from the "can't find what you're looking for" mailto.
  CONNECTORS_ORCID_PROFILE_LINK_CLICKED: 'Connectors Orcid Profile Link Clicked',
  CONNECTORS_CONTACT_US_CLICKED: 'Connectors Contact Us Clicked',
  GRAPH_OPENED: 'Graph Opened',
  GRAPH_NODE_SELECTED: 'Graph Node Selected',
  GRAPH_EDGE_SELECTED: 'Graph Edge Selected',
  GRAPH_FILTER_CHANGED: 'Graph Filter Changed',
  GRAPH_SEARCH_USED: 'Graph Search Used',
  GRAPH_LIST_VIEW_TOGGLED: 'Graph List View Toggled',
  GRAPH_RUNS_TOGGLED: 'Graph Runs Toggled',

  // MCP External Sign-in (WorkOS Standalone Connect)
  MCP_SIGNIN_LANDED: 'mcp.signin.landed',
  MCP_SIGNIN_WORKSPACE_PICKED: 'mcp.signin.workspace_picked',
  MCP_SIGNIN_COMPLETED: 'mcp.signin.completed',
  MCP_SIGNIN_FAILED: 'mcp.signin.failed',

  // MCP Help / AI app access tab
  MCP_HELP_TAB_VIEWED: 'mcp.help.tab_viewed',
  MCP_HELP_SERVER_URL_COPIED: 'mcp.help.server_url_copied',

  // Connector Import pipeline — backend-emitted from Cloud Tasks job handlers.
  // These mirror CONNECTOR_IMPORT_EVENTS in @bower/shared; the frontend
  // constants are declared here for grep-affinity and future FE use.
  // distinctId is the run's userId (passed explicitly — AsyncLocalStorage
  // does not reliably propagate into background job trees).
  CONNECTOR_IMPORT_STARTED: 'Connector Import Started',
  CONNECTOR_IMPORT_COMPLETED: 'Connector Import Completed',
  CONNECTOR_IMPORT_FAILED: 'Connector Import Failed',
} as const;
